import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { usePrevious } from './usePrevious';

/**
 * useMutatedState is like `useState`, except if the initial value changes, we'll
 * update the state by passing the initial value through the mutator function.
 */
export function useMutatedState<T, V>(
  value: V,
  mutator: (value: V) => T,
): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState(mutator(value));

  const prevValue = usePrevious(value);
  useEffect(() => {
    if (
      value instanceof Date &&
      prevValue instanceof Date &&
      value.valueOf() !== prevValue.valueOf()
    ) {
      setState(mutator(value));
    } else if (value !== prevValue) {
      setState(mutator(value));
    }
  }, [value, prevValue, mutator]);

  return [state, setState];
}
